
import { useState, useEffect } from "react";
import { ExtendedCourse } from "@/types/courses";
import { getCourses, dbCourseToCourse } from "@/integrations/supabase/courseService";
import { 
  getUserCourses, 
  getFavoriteCourses,
  toggleFavoriteCourse, 
  addCourseToUser,
  removeCourseFromUser,
  ProfileCourseLink
} from "@/integrations/supabase/profileCourseService";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Course } from "@/types";

export const useCourses = () => {
  const [allCourses, setAllCourses] = useState<ExtendedCourse[]>([]);
  const [userCourses, setUserCourses] = useState<ExtendedCourse[]>([]);
  const [favoriteCourses, setFavoriteCourses] = useState<ExtendedCourse[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper function to convert ProfileCourseLink array to ExtendedCourse array
  const convertProfileCourseLinks = (links: ProfileCourseLink[]): ExtendedCourse[] => {
    return links.map(link => {
      // First convert the database course to the Course type
      const course = dbCourseToCourse(link);
      
      // Return as ExtendedCourse with favorite properties
      return {
        ...course,
        is_favorite: link.is_favorite,
        link_id: link.id,
        city: link.city,
        state: link.state,
        available_tees: link.available_tees || [],
        teeOptions: link.available_tees || []
      } as ExtendedCourse;
    });
  };

  // Function to refresh user courses
  const refreshUserCourses = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const userCoursesData = await getUserCourses(user.id);
      const favoriteCoursesData = await getFavoriteCourses(user.id);
      
      // Convert to ExtendedCourse type before setting state
      setUserCourses(convertProfileCourseLinks(userCoursesData));
      setFavoriteCourses(convertProfileCourseLinks(favoriteCoursesData));
    }
  };

  // Fetch all courses
  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Get all courses
        const coursesData = await getCourses();
        
        // Convert raw DB courses to ExtendedCourse type
        const extendedCourses: ExtendedCourse[] = coursesData.map(course => ({
          ...dbCourseToCourse(course),
          city: course.city,
          state: course.state,
          available_tees: course.available_tees || [],
          teeOptions: course.teeOptions || course.available_tees || []
        }));
        
        setAllCourses(extendedCourses);
        
        // Get user's courses and favorites
        await refreshUserCourses();
      } else {
        setAllCourses([]);
        setUserCourses([]);
        setFavoriteCourses([]);
      }
      
      setLoading(false);
    };
    
    fetchCourses();
  }, []);

  // Function to toggle favorite status
  const handleToggleFavorite = async (course: ExtendedCourse) => {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    if (!course.link_id) {
      // User hasn't added this course yet, add it first
      const { data } = await addCourseToUser(user.id, course.id, true);
      if (data) {
        toast({
          title: "Course Favorited",
          description: `${course.name} has been added to your favorites.`,
        });
        
        // Refresh user courses
        await refreshUserCourses();
      }
    } else {
      // We have a link_id, so the course is already in the user's list
      const newFavoriteStatus = !course.is_favorite;
      
      if (newFavoriteStatus) {
        // Mark as favorite
        const { data } = await toggleFavoriteCourse(course.link_id, true);
        if (data) {
          toast({
            title: "Course Favorited",
            description: `${course.name} has been added to your favorites.`,
          });
          
          // Refresh the courses to update the UI
          await refreshUserCourses();
        }
      } else {
        // Remove from favorites
        const { data } = await toggleFavoriteCourse(course.link_id, false);
        if (data) {
          toast({
            title: "Course Unfavorited",
            description: `${course.name} has been removed from your favorites.`,
          });
          
          // Refresh the courses to update the UI
          await refreshUserCourses();
        }
      }
    }
  };

  return {
    allCourses,
    userCourses,
    favoriteCourses,
    loading,
    handleToggleFavorite,
    refreshUserCourses
  };
};
